import { useEffect, useState } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import ActPaceTable from '@/components/act/ActPaceTable'
import ActStrategyTable from '@/components/act/ActStrategyTable'
import ActSummaryTiles, { CalibrationGrid } from '@/components/act/ActSummaryTiles'
import { ActInsights, browserTimezone, formatDuration, percent } from '@/lib/actModes'

/**
 * The parent-side rollup of ACT practice. Read-only: the work is his, this just
 * shows whether the pace is closing on the real clock.
 */
export default function ActInsightsPanel({ childId }: { childId: number }) {
  const [insights, setInsights] = useState<ActInsights | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    apiFetch<ActInsights>(
      `/act/insights?childId=${childId}&days=30&timezone=${encodeURIComponent(browserTimezone())}`
    )
      .then(data => { if (!cancelled) setInsights(data) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [childId])

  if (loading || !insights || insights.summary.questionsCompleted === 0) return null

  const summary = insights.summary

  return (
    <div className="border border-border rounded-xl p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">ACT practice</h2>
          <p className="text-xs text-muted-foreground">
            {summary.questionsCompleted} questions in 30 days
            {insights.dayStreak > 0 && ` · 🔥 ${insights.dayStreak}-day streak`}
          </p>
        </div>
        <button
          onClick={() => setExpanded(v => !v)}
          className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-accent transition min-h-[36px]"
        >
          {expanded ? 'Less' : 'More'}
        </button>
      </div>

      <ActSummaryTiles summary={summary} compact={false} />

      <p className="text-sm text-muted-foreground">
        Averaging {formatDuration(summary.avgActualSeconds)} a question
        {summary.pacedQuestions > 0 &&
          `, and ${percent(summary.paceAccuracy)} of full-section questions came in inside their budget`}
        .
        {insights.trend && (
          insights.trend.timeErrorImproved
            ? ' Estimates are getting tighter over this window.'
            : ' Estimates are not tightening yet over this window.'
        )}
      </p>

      {expanded && (
        <div className="space-y-5 pt-2 border-t border-border">
          <section>
            <h3 className="text-sm font-semibold mb-2">Pace vs. the real clock</h3>
            <ActPaceTable pace={insights.pace} />
          </section>

          {summary.predictionQuestions > 0 && (
            <section>
              <h3 className="text-sm font-semibold mb-2">Confidence vs. reality</h3>
              <CalibrationGrid summary={summary} />
            </section>
          )}

          {insights.strategies.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold mb-2">Moves that land</h3>
              <ActStrategyTable strategies={insights.strategies} />
            </section>
          )}
        </div>
      )}
    </div>
  )
}
